import * as vp from 'vopay';
import moment from 'moment';
import { PaymentUpdate } from './types';
import styles from './AccountingSystemOpen.module.scss';
import Truncate from '../../../UI/Truncate';
import { JSX } from 'react';

const { Box, Icon, Button } = vp;

export const updateCustomerData = (
  values: PaymentUpdate,
  originalValues: vp.TemporaryBillsOrInvoicesRails,
  Country: string,
  type: 'Payable' | 'Receivable',
  amount: string,
  scheduleDate: string,
  creditNotes?: string,
): vp.TemporaryBillsOrInvoicesRails => ({
  ...originalValues,
  Customer: {
    ...originalValues.Customer,
    BankAccount: {
      ...originalValues.Customer?.BankAccount,
      ...(['Bank Account', 'RTP', 'VoPay Instant'].includes(values.PaymentMethod)
        ? {
            AccountNumber: values.AccountNumber,
            BranchTransitNumber: values.BankTransitNumber,
            FinancialInstitutionNumber: values.FinancialInstitutionNumber,
            PhoneNumber: values.PhoneNumber,
            City: values.City,
            Address: values.Address,
            State: values.State,
            ZipCode: values.ZipCode,
            IdentificationNumber: values.IdentificationNumber,
          }
        : {}),
    },
    Interac: {
      ...originalValues.Customer?.Interac,
      ...(['Interac e-Transfer', 'Interac e-Transfer Money Request'].includes(values.PaymentMethod)
        ? {
            EmailAddress: values.InteracMethod === 'Email Address' ? values.Email : undefined,
            PhoneNumber: values.InteracMethod === 'Phone Number' ? values.Phone : undefined,
            Question: values.Question,
            Answer: values.Answer,
            Language: values.Language,
          }
        : {}),
    },
    DebitCard: {
      ...originalValues.Customer?.DebitCard,
      ...(values.PaymentMethod === 'Debit Card' ? { Token: values.TokenDebit } : {}),
    },
    CreditCard: {
      ...originalValues.Customer?.CreditCard,
      ...(values.PaymentMethod === 'Credit Card'
        ? {
            Token: values.TokenCredit,
            CreditCardNumber: values.CreditCardNumber,
            CreditCardBrand: values.CreditCardBrand,
            CreditCardExpiryYear: values.CreditCardExpiryYear,
            CreditCardExpiryMonth: values.CreditCardExpiryMonth,
          }
        : {}),
    },
  },
  hasInterac: ['Interac e-Transfer', 'Interac e-Transfer Money Request'].includes(values.PaymentMethod)
    ? true
    : originalValues.hasInterac,
  hasBankAccount: ['Bank Account', 'RTP', 'VoPay Instant'].includes(values.PaymentMethod)
    ? true
    : originalValues.hasBankAccount,
  hasCreditCard: values.PaymentMethod === 'Credit Card' ? true : originalValues.hasCreditCard,
  hasDebitCard: values.PaymentMethod === 'Debit Card' ? true : originalValues.hasDebitCard,
  paymentMethodUser: values.PaymentMethod,
  PaymentRail: vp.getPaymentRailsAccountingSoftware(values.PaymentMethod, Country, type),
  PaidAmount: vp.sanitizeToNumeric(String(amount)) || String(amount),
  scheduledDate: moment(scheduleDate).isBefore(moment().endOf('day')) ? '' : scheduleDate,
  creditNotes,
});
export const hasAdd = (
  options: { hasBankAccount: boolean; hasCreditCard: boolean; hasDebitCard: boolean; hasInterac: boolean },
  PaymentMethod: 'Payable' | 'Receivable',
): boolean => {
  const { hasBankAccount, hasCreditCard, hasDebitCard, hasInterac } = options;

  // Determine if each payment method is available based on the options and PaymentMethod
  const noBankAccount = !hasBankAccount;
  const noCreditCard = !hasCreditCard || (PaymentMethod === 'Receivable' && !hasCreditCard);
  const noDebitCard = !hasDebitCard;
  const noInterac = !hasInterac;

  // Return true if any of the payment methods are not available
  return noBankAccount || noCreditCard || noDebitCard || noInterac;
};
export type ExtraOptionsInfo = {
  visible: boolean;
  renderedValue: JSX.Element;
  renderedLabel: (options: { close: () => void }) => JSX.Element;
};
export function getAvailablePaymentNew(
  options: vp.TemporaryBillsOrInvoicesRails | undefined,
  PaymentMethod: 'Payable' | 'Receivable',
  onAdd: () => void,
) {
  const hasAddNew = hasAdd(
    options || { hasBankAccount: false, hasCreditCard: false, hasDebitCard: false, hasInterac: false },
    PaymentMethod,
  );
  if (hasAddNew) {
    return (
      <div className={[styles.dropdownOption, styles.dropdownOptionNew].join(' ')}>
        <Box flx="col" base="5" gap="sm" width="100%">
          <div className={styles.dropdownOptionNewObject}>
            <div className={styles.dropdownHeader}>New</div>
            <Box flx="row" base="8" ai="center" jc="sb" pt="sm" width="100%">
              <Button
                label="Payment Method"
                color="secondary"
                height="28px"
                iconLocation="left"
                justifyLabel="fs"
                iconHeight="16px"
                iconWidth="16px"
                icon="add"
                onClick={() => {
                  onAdd();
                }}
              />
            </Box>
          </div>
        </Box>
      </div>
    );
  }
  return undefined;
}
export function getAvailablePaymentMethodsDropdown(
  options: vp.TemporaryBillsOrInvoicesRails | undefined,
  country: string,
  PaymentMethod: 'Payable' | 'Receivable',
  onEdit: (value: string) => void,
): vp.SelectOption<ExtraOptionsInfo>[] {
  const customOptionsList: vp.SelectOption<ExtraOptionsInfo>[] = [
    {
      renderedLabel: ({ close }) => (
        <div className={styles.dropdownOption}>
          <Box flx="col" base="5" gap="sm" width="100%">
            <div className={styles.dropdownHeader}>Bank Account</div>
            <Box flx="row" base="8" ps="smd" pe="sm" ai="center" jc="sb" width="100%">
              <Box flx="row" base="8" gap="sm" ai="center">
                <Icon icon="bank" height="18px" width="18px" color="none" />{' '}
                <span>
                  {options?.Customer?.BankAccount?.FinancialInstitutionNumber}{' '}
                  {options?.Customer?.BankAccount?.BranchTransitNumber}{' '}
                  {vp.getMaskedNumber(options?.Customer?.BankAccount?.AccountNumber || '')}
                </span>
              </Box>
              <button
                type="button"
                className={styles.editButton}
                onClick={() => {
                  close();
                  onEdit('Bank Account');
                }}
              >
                Edit
              </button>
            </Box>
          </Box>
        </div>
      ),
      label: `${options?.Customer?.BankAccount?.FinancialInstitutionNumber || ''} 
            ${options?.Customer?.BankAccount?.BranchTransitNumber || ''} 
            ${vp.getMaskedNumber(options?.Customer?.BankAccount?.AccountNumber || '')}`,
      value: 'Bank Account',
      renderedValue: (
        <div className={styles.dropDownValue}>
          <Box flx="row" base="8" gap="sm" ai="center">
            <Icon icon="bank" height="16px" width="16px" color="none" />{' '}
            <span>
              {options?.Customer?.BankAccount?.FinancialInstitutionNumber}{' '}
              {options?.Customer?.BankAccount?.BranchTransitNumber}{' '}
              {vp.getMaskedNumber(options?.Customer?.BankAccount?.AccountNumber || '')}
            </span>
          </Box>
        </div>
      ),
      visible: !!options?.hasBankAccount,
      style: undefined,
    },
    {
      renderedLabel: ({ close }) => (
        <div className={styles.dropdownOption}>
          <Box flx="col" base="5" gap="sm" width="100%">
            <div className={styles.dropdownHeader}>VoPay Instant</div>
            <Box flx="row" base="8" ps="smd" pe="sm" ai="center" jc="sb" width="100%">
              <Box flx="row" base="8" gap="sm" ai="center">
                <Icon icon="bank" height="18px" width="18px" color="none" />{' '}
                <span>
                  {options?.Customer?.BankAccount?.FinancialInstitutionNumber}{' '}
                  {options?.Customer?.BankAccount?.BranchTransitNumber}{' '}
                  {vp.getMaskedNumber(options?.Customer?.BankAccount?.AccountNumber || '')}
                </span>
              </Box>
              <button
                type="button"
                className={styles.editButton}
                onClick={() => {
                  close();
                  onEdit('VoPay Instant');
                }}
              >
                Edit
              </button>
            </Box>
          </Box>
        </div>
      ),
      value: 'VoPay Instant',
      label: `${options?.Customer?.BankAccount?.FinancialInstitutionNumber || ''} 
      ${options?.Customer?.BankAccount?.BranchTransitNumber || ''} 
      ${vp.getMaskedNumber(options?.Customer?.BankAccount?.AccountNumber || '')}`,
      renderedValue: (
        <div className={styles.dropDownValue}>
          <Box flx="row" base="8" gap="sm" ai="center">
            <Icon icon="bank" height="16px" width="16px" color="none" />{' '}
            <span>
              {options?.Customer?.BankAccount?.FinancialInstitutionNumber}{' '}
              {options?.Customer?.BankAccount?.BranchTransitNumber}{' '}
              {vp.getMaskedNumber(options?.Customer?.BankAccount?.AccountNumber || '')}
            </span>
          </Box>
        </div>
      ),
      visible: !!options?.hasBankAccount && country === 'CA' && PaymentMethod === 'Payable',
      style: undefined,
    },

    {
      renderedLabel: ({ close }) => (
        <div className={styles.dropdownOption}>
          <Box flx="col" base="5" gap="sm" width="100%">
            <div className={styles.dropdownHeader}>RTP</div>
            <Box flx="row" base="8" ps="smd" pe="sm" ai="center" jc="sb" width="100%">
              <Box flx="row" base="8" gap="sm" ai="center">
                <Icon icon="bank" height="18px" width="18px" color="none" />{' '}
                <span>
                  {options?.Customer?.BankAccount?.FinancialInstitutionNumber}{' '}
                  {options?.Customer?.BankAccount?.BranchTransitNumber}{' '}
                  {vp.getMaskedNumber(options?.Customer?.BankAccount?.AccountNumber || '')}
                </span>
              </Box>
              <button
                type="button"
                className={styles.editButton}
                onClick={() => {
                  close();
                  onEdit('RTP');
                }}
              >
                Edit
              </button>
            </Box>
          </Box>
        </div>
      ),
      value: 'RTP',
      label: `${options?.Customer?.BankAccount?.FinancialInstitutionNumber || ''} 
      ${options?.Customer?.BankAccount?.BranchTransitNumber || ''} 
      ${vp.getMaskedNumber(options?.Customer?.BankAccount?.AccountNumber || '')}`,
      renderedValue: (
        <div className={styles.dropDownValue}>
          <Box flx="row" base="8" gap="sm" ai="center">
            <Icon icon="bank" height="16px" width="16px" color="none" />{' '}
            <span>
              {options?.Customer?.BankAccount?.FinancialInstitutionNumber}{' '}
              {options?.Customer?.BankAccount?.BranchTransitNumber}{' '}
              {vp.getMaskedNumber(options?.Customer?.BankAccount?.AccountNumber || '')}
            </span>
          </Box>
        </div>
      ),
      visible: !!options?.hasBankAccount && country === 'US' && PaymentMethod === 'Payable',
      style: undefined,
    },

    {
      renderedLabel: ({ close }) => (
        <div className={styles.dropdownOption}>
          <Box flx="col" base="5" gap="sm" width="100%">
            <div className={styles.dropdownHeader}>Debit Card</div>
            <Box flx="row" base="8" ps="smd" pe="sm" ai="center" jc="sb" width="100%">
              <Box flx="row" base="8" gap="sm" ai="center">
                <Icon icon="card" height="18px" width="18px" color="none" />{' '}
                <span>{vp.getMaskedNumber(options?.Customer?.DebitCard?.CardNumber || '')}</span>
              </Box>
              <button
                type="button"
                className={styles.editButton}
                onClick={() => {
                  close();
                  onEdit('Debit Card');
                }}
              >
                Edit
              </button>
            </Box>
          </Box>
        </div>
      ),
      label: vp.getMaskedNumber(options?.Customer?.DebitCard?.CardNumber || ''),
      value: 'Debit Card',
      renderedValue: (
        <div className={styles.dropDownValue}>
          <Box flx="row" base="8" gap="sm" ai="center">
            <Icon icon="card" height="16px" width="16px" color="none" />{' '}
            <span>{vp.getMaskedNumber(options?.Customer?.DebitCard?.CardNumber || '')}</span>
          </Box>
        </div>
      ),
      visible: !!options?.hasDebitCard && PaymentMethod === 'Payable',
      style: undefined,
    },
    {
      renderedLabel: ({ close }) => (
        <div className={styles.dropdownOption}>
          <Box flx="col" base="5" gap="sm" width="100%">
            <div className={styles.dropdownHeader}>Credit Card</div>
            <Box flx="row" base="8" ps="smd" pe="sm" ai="center" jc="sb" width="100%">
              <Box flx="row" base="8" gap="sm" ai="center">
                <Icon icon="card" height="18px" width="18px" color="none" />{' '}
                <span>{vp.getMaskedNumber(options?.Customer?.CreditCard?.CreditCardNumber || '')}</span>
              </Box>
              <button
                type="button"
                className={styles.editButton}
                onClick={() => {
                  close();
                  onEdit('Credit Card');
                }}
              >
                Edit
              </button>
            </Box>
          </Box>
        </div>
      ),
      label: vp.getMaskedNumber(options?.Customer?.CreditCard?.CreditCardNumber || ''),
      value: 'Credit Card',
      renderedValue: (
        <div className={styles.dropDownValue}>
          <Box flx="row" base="8" gap="sm" ai="center">
            <Icon icon="card" height="16px" width="16px" color="none" />{' '}
            <span>{vp.getMaskedNumber(options?.Customer?.CreditCard?.CreditCardNumber || '')}</span>
          </Box>
        </div>
      ),
      visible: !!options?.hasCreditCard && PaymentMethod === 'Receivable',
      style: undefined,
    },
    {
      renderedLabel: ({ close }) => (
        <div className={styles.dropdownOption}>
          <Box flx="col" base="5" gap="sm" width="100%">
            <div className={styles.dropdownHeader}>Interac e-Transfer Money Request</div>
            <Box flx="row" base="8" ps="smd" pe="sm" ai="center" jc="sb" width="100%">
              <Box flx="row" base="8" gap="sm" ai="center">
                <Icon icon="interacColor" height="18px" width="18px" color="none" />{' '}
                <span>
                  <Truncate
                    text={
                      options?.Customer?.Interac?.EmailAddress ||
                      options?.Customer?.Interac?.PhoneNumber ||
                      options?.Customer?.Email ||
                      ''
                    }
                    maxLength={20}
                    truncateEnding="..."
                  />
                </span>
              </Box>
              <button
                type="button"
                className={styles.editButton}
                onClick={() => {
                  close();
                  onEdit('Interac e-Transfer Money Request');
                }}
              >
                Edit
              </button>
            </Box>
          </Box>
        </div>
      ),
      label:
        options?.Customer?.Interac?.EmailAddress ||
        options?.Customer?.Interac?.PhoneNumber ||
        options?.Customer?.Email ||
        '',
      value: 'Interac e-Transfer Money Request',
      renderedValue: (
        <div className={styles.dropDownValue}>
          <Box flx="row" base="8" gap="sm" ai="center">
            <Icon icon="interacColor" height="16px" width="16px" color="none" />{' '}
            <span>
              <Truncate
                text={
                  options?.Customer?.Interac?.EmailAddress ||
                  options?.Customer?.Interac?.PhoneNumber ||
                  options?.Customer?.Email ||
                  ''
                }
                maxLength={20}
                truncateEnding="..."
              />
            </span>
          </Box>
        </div>
      ),
      visible: !!options?.hasInterac && country === 'CA' && PaymentMethod === 'Receivable',
      style: undefined,
    },
    {
      renderedLabel: ({ close }) => (
        <div className={styles.dropdownOption}>
          <Box flx="col" base="5" gap="sm" width="100%">
            <div className={styles.dropdownHeader}>Interac e-Transfer</div>
            <Box flx="row" base="8" ps="smd" pe="sm" ai="center" jc="sb" width="100%">
              <Box flx="row" base="8" gap="sm" ai="center">
                <Icon icon="interacColor" height="18px" width="18px" color="none" />{' '}
                <span>
                  <Truncate
                    text={
                      options?.Customer?.Interac?.EmailAddress ||
                      options?.Customer?.Interac?.PhoneNumber ||
                      options?.Customer?.Email ||
                      ''
                    }
                    maxLength={20}
                    truncateEnding="..."
                  />
                </span>
              </Box>
              <button
                type="button"
                className={styles.editButton}
                onClick={() => {
                  close();
                  onEdit('Interac e-Transfer');
                }}
              >
                Edit
              </button>
            </Box>
          </Box>
        </div>
      ),
      label:
        options?.Customer?.Interac?.EmailAddress ||
        options?.Customer?.Interac?.PhoneNumber ||
        options?.Customer?.Email ||
        '',
      value: 'Interac e-Transfer',
      renderedValue: (
        <div className={styles.dropDownValue}>
          <Box flx="row" base="8" gap="sm" ai="center">
            <Icon icon="interacColor" height="16px" width="16px" color="none" />{' '}
            <span>
              <Truncate
                text={
                  options?.Customer?.Interac?.EmailAddress ||
                  options?.Customer?.Interac?.PhoneNumber ||
                  options?.Customer?.Email ||
                  ''
                }
                maxLength={20}
                truncateEnding="..."
              />
            </span>
          </Box>
        </div>
      ),
      visible: !!options?.hasInterac && country === 'CA' && PaymentMethod === 'Payable',
      style: undefined,
    },
  ];
  return customOptionsList;
}
export function getRequiredIcon(): JSX.Element {
  return <Icon icon="asterisk" size="xsm" height="15px" width="15px" color="error" />;
}
// todo fix labels
export function getAvailablePaymentMethodLabel(item: vp.SelectOption<ExtraOptionsInfo> | null): React.ReactNode {
  if (item) return item.renderedValue;
  return '';
}
export function getAvailablePaymentMethodsRenderOptions(
  item: vp.SelectOption<ExtraOptionsInfo>,
  { Option, close }: vp.SelectRenderOptionArgs,
): React.ReactNode {
  return <Option>{item.renderedLabel({ close })}</Option>;
}
export function getAvailablePaymentMethodVisibility(item: vp.SelectOption<ExtraOptionsInfo> | null): boolean {
  if (!item) return false;
  return item.visible;
}
